import { useCallback, useEffect, useState } from "react";
import { KeyRound, Pencil, Trash2, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Team() {
  const { user, restaurant, saveSession } = useAuth();
  const [members, setMembers] = useState([]);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinValue, setPinValue] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setMembers(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load team"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove member"));
    }
  };

  const doRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await api.patch(`/team/${renameTarget.id}`, { name: renameValue.trim() });
      toast.success("Name updated");
      if (renameTarget.id === user.id) {
        const { data } = await api.get("/auth/me");
        saveSession(data);
      }
      setRenameTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update name"));
    } finally {
      setRenaming(false);
    }
  };

  const doResetPin = async (generate) => {
    if (!pinTarget) return;
    setPinBusy(true);
    try {
      const { data } = await api.post(`/team/${pinTarget.id}/reset-pin`, generate ? {} : { pin: pinValue.trim() });
      setPinResult(data.pin);
      toast.success(`PIN updated for ${pinTarget.name}`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not reset PIN"));
    } finally {
      setPinBusy(false);
    }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const deleteSelected = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected member${selected.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setDeleting(true);
    const results = await Promise.allSettled(selected.map((id) => api.delete(`/team/${id}`)));
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed) {
      toast.error(`${failed} member(s) could not be removed`);
    } else {
      toast.success(`${selected.length} member(s) removed`);
    }
    setSelected([]);
    setDeleting(false);
    load();
  };

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="team-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Team</h1>
      <p className="text-sm text-slate-500 mt-1">Everyone who can clock in at your business.</p>

      <div className="mt-6 p-4 sm:p-6 rounded-2xl bg-[#FDF0EB] border border-terra-400/40 flex items-center gap-4 flex-wrap" data-testid="join-code-banner">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-terra-600 uppercase tracking-wider mb-1">Staff join code</div>
          <div className="font-mono font-bold text-2xl text-terra-600 tracking-[0.3em]" data-testid="team-join-code">{restaurant?.join_code}</div>
        </div>
        <Link to="/join" className="h-11 px-4 rounded-xl border border-terra-400/50 text-terra-600 font-medium flex items-center gap-2 hover:bg-[#FDF0EB] transition-colors" data-testid="team-invite-link">
          <UserPlus className="w-4 h-4" /> Add Staff
        </Link>
      </div>

      {selected.length > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-[#FDF0EB] border border-terra-400/40 flex items-center gap-3 flex-wrap" data-testid="bulk-delete-bar">
          <span className="text-sm text-terra-600 font-medium" data-testid="bulk-delete-count">{selected.length} selected</span>
          <button
            onClick={deleteSelected}
            disabled={deleting}
            data-testid="bulk-delete-button"
            className="h-10 px-4 rounded-xl bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? "Removing…" : "Delete Selected"}
          </button>
          <button onClick={() => setSelected([])} data-testid="bulk-delete-clear-button" className="text-sm text-slate-500 hover:text-terra-600 transition-colors">
            Clear
          </button>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-white border border-[#E4DDD3] overflow-hidden">
        <div className="divide-y divide-[#E4DDD3]/60">
          {members.map((m) => (
            <div key={m.id} className="px-4 sm:px-6 py-4 flex items-center gap-3" data-testid={`team-member-row-${m.id}`}>
              {m.id !== user.id ? (
                <input
                  type="checkbox"
                  checked={selected.includes(m.id)}
                  onChange={() => toggleSelect(m.id)}
                  data-testid={`select-member-checkbox-${m.id}`}
                  className="w-4 h-4 accent-terra-500 shrink-0 cursor-pointer"
                  title="Select for deletion"
                />
              ) : (
                <span className="w-4 shrink-0" />
              )}
              <div className="w-10 h-10 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] grid place-items-center font-display font-bold text-terra-600">
                {m.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1A1D20]">{m.name}</div>
                {m.email && <div className="text-xs text-slate-500">{m.email}</div>}
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full border uppercase tracking-wider ${m.primary_role === "Manager" ? "bg-[#FDF0EB] border-terra-400/50 text-terra-600" : "bg-white border-[#E4DDD3] text-slate-600"}`}>
                {m.primary_role}
              </span>
              <button
                onClick={() => { setRenameTarget(m); setRenameValue(m.name); }}
                className="p-2 rounded-lg text-slate-500 hover:text-terra-600 hover:bg-[#FDF0EB] transition-colors"
                data-testid={`rename-member-button-${m.id}`}
                title="Rename"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {m.primary_role !== "Manager" && (
                <button
                  onClick={() => { setPinTarget(m); setPinValue(""); setPinResult(null); }}
                  className="p-2 rounded-lg text-slate-500 hover:text-terra-600 hover:bg-[#FDF0EB] transition-colors"
                  data-testid={`reset-pin-button-${m.id}`}
                  title="Reset PIN"
                >
                  <KeyRound className="w-4 h-4" />
                </button>
              )}
              {m.id !== user.id && (
                <button onClick={() => remove(m)} className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`remove-member-button-${m.id}`} title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!renameTarget} onOpenChange={() => setRenameTarget(null)}>
        <DialogContent className="bg-white border-[#E4DDD3]" data-testid="rename-member-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#1A1D20] font-display">Rename {renameTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doRename()}
              placeholder="New name"
              data-testid="rename-member-input"
              className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
            />
            <button
              onClick={doRename}
              disabled={renaming || !renameValue.trim()}
              data-testid="rename-member-save-button"
              className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
            >
              {renaming ? "Saving…" : "Save Name"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={() => setPinTarget(null)}>
        <DialogContent className="bg-white border-[#E4DDD3]" data-testid="reset-pin-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#1A1D20] font-display">Reset PIN — {pinTarget?.name}</DialogTitle>
          </DialogHeader>
          {pinResult ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Share this new PIN with {pinTarget?.name} — it won't be shown again.</p>
              <div className="text-center font-mono font-bold text-4xl text-terra-600 tracking-[0.4em]" data-testid="reset-pin-result">{pinResult}</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(pinResult); toast.success("PIN copied"); }}
                  data-testid="reset-pin-copy-button"
                  className="h-11 rounded-xl border border-terra-400/50 text-terra-600 text-sm font-medium hover:bg-[#FDF0EB] transition-colors"
                >
                  Copy PIN
                </button>
                <button
                  onClick={() => setPinTarget(null)}
                  data-testid="reset-pin-done-button"
                  className="h-11 rounded-xl bg-terra-500 text-white text-sm font-bold hover:bg-terra-600 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <input
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="New 4-digit PIN"
                inputMode="numeric"
                data-testid="reset-pin-input"
                className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 font-mono text-center text-xl tracking-[0.4em] focus:outline-none focus:border-terra-400/60"
              />
              <button
                onClick={() => doResetPin(false)}
                disabled={pinBusy || pinValue.length !== 4}
                data-testid="reset-pin-save-button"
                className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
              >
                {pinBusy ? "Saving…" : "Set PIN"}
              </button>
              <button
                onClick={() => doResetPin(true)}
                disabled={pinBusy}
                data-testid="reset-pin-generate-button"
                className="w-full h-11 rounded-xl border border-terra-400/50 text-terra-600 text-sm font-medium hover:bg-[#FDF0EB] disabled:opacity-50 transition-colors"
              >
                Generate a random PIN
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
